// Mitmachen: Live-Abstimmung und Mitraten aus der Suite heraus.
//
// Dieselbe Arbeit wie im Studio, nur mit mehr Platz: Fragen vorbereiten, Vorlagen
// für wiederkehrende Fragen anlegen, starten und beenden, und hinterher sehen,
// wie die Hörer abgestimmt haben.
//
// Sichtbar ist die Seite nur, wenn mindestens eines der beiden Module
// eingeschaltet ist (Aussehen → Mitmach-Module).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api, pollApi, quizApi, type Poll, type QuizQuestion, type QuizScore } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { errorText } from "../components/streaming-ui";
import { ErgebnisBalken, KanalWahl, Meldungen, ModulAus, StandSchild, Sym } from "../components/social-ui";
import { Badge, Card, EmptyState, LiveDot, Page, PageHeader, Tabs } from "../components/ui";
import type { Channel, LibraryItem } from "../types";

type Draft = { label: string; item: LibraryItem | null; correct: boolean };

const emptyDraft = (): Draft[] => [
  { label: "", item: null, correct: true },
  { label: "", item: null, correct: false },
];

/** Titel aus der Mediathek an eine Antwort hängen – für „Gewinner in die Queue“. */
function TrackPicker({ value, onPick }: { value: LibraryItem | null; onPick: (item: LibraryItem | null) => void }): JSX.Element {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<LibraryItem[]>([]);

  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); return; }
    const timer = setTimeout(() => {
      api.listLibrary({ q: q.trim(), limit: 5 }).then(setHits).catch(() => setHits([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  if (value) {
    return (
      <div className="flex min-w-0 items-center gap-2 text-xs">
        <span className="inline-flex min-w-0 items-center gap-1.5 rounded-md bg-brand-primary/10 px-2 py-1 text-brand-primary">
          <Sym name="music" className="h-3.5 w-3.5" />
          <span className="truncate">{value.title}{value.artist ? ` – ${value.artist}` : ""}</span>
        </span>
        <button type="button" onClick={() => onPick(null)} className="text-muted underline-offset-2 hover:text-primary hover:underline">lösen</button>
      </div>
    );
  }
  return (
    <div className="relative text-xs">
      <Sym name="music" className="pointer-events-none absolute left-2.5 top-2 h-3.5 w-3.5 text-muted" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Titel aus der Mediathek (freiwillig) …"
        className="input h-8 pl-8 text-xs" />
      {hits.length > 0 && (
        <ul className="mt-1 divide-y divide-default overflow-hidden rounded-lg border border-default bg-card shadow-md">
          {hits.map((h) => (
            <li key={h.id}>
              <button type="button" onClick={() => { onPick(h); setQ(""); setHits([]); }} className="w-full px-3 py-1.5 text-left text-primary hover:bg-surface-hover" style={{ minHeight: 0 }}>
                {h.title}{h.artist ? ` – ${h.artist}` : ""}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Frageformular für beide Module: 2–4 Antworten, beim Quiz eine davon richtig. */
function QuestionForm({ kind, busy, onCreate }: {
  kind: "poll" | "quiz";
  busy: boolean;
  onCreate: (question: string, options: Draft[], asTemplate: boolean) => void;
}): JSX.Element {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState<Draft[]>(emptyDraft());
  const [asTemplate, setAsTemplate] = useState(false);

  const setAt = (i: number, patch: Partial<Draft>): void =>
    setOptions((list) => list.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));

  return (
    <form
      className="card space-y-4 p-5"
      onSubmit={(e) => {
        e.preventDefault();
        onCreate(question, options, asTemplate);
        setQuestion("");
        setOptions(emptyDraft());
        setAsTemplate(false);
      }}
    >
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-primary/10 text-brand-primary"><Sym name="plus" className="h-4 w-4" /></span>
        <h3 className="card-title">Neue Frage</h3>
      </div>
      <div>
        <label className="field-label" htmlFor={`frage-${kind}`}>Frage</label>
        <input id={`frage-${kind}`} value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={200}
          placeholder={kind === "poll" ? "Was wollt ihr wissen?" : "Die Quizfrage"}
          className="input" />
      </div>
      <div>
        <div className="field-label">Antworten</div>
      <ul className="space-y-2">
        {options.map((o, i) => (
          <li key={i} className={`space-y-2 rounded-lg border p-2.5 ${kind === "quiz" && o.correct ? "border-emerald-500/40 bg-emerald-500/[0.04]" : "border-default bg-input"}`}>
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-card text-xs font-semibold text-muted" style={{ boxShadow: "inset 0 0 0 1px var(--border)" }}>{String.fromCharCode(65 + i)}</span>
              {kind === "quiz" && (
                <label className={`flex flex-shrink-0 items-center gap-1 text-xs ${o.correct ? "font-medium text-emerald-700 dark:text-emerald-300" : "text-muted"}`} title="Das ist die richtige Antwort">
                  <input type="radio" name="correct" checked={o.correct}
                    onChange={() => setOptions((list) => list.map((x, idx) => ({ ...x, correct: idx === i })))} />
                  richtig
                </label>
              )}
              <input value={o.label} onChange={(e) => setAt(i, { label: e.target.value })} maxLength={80}
                placeholder={`Antwort ${i + 1}`} className="input h-8 min-w-0 flex-1 bg-card" />
              {options.length > 2 && (
                <button type="button" onClick={() => setOptions((list) => list.filter((_, idx) => idx !== i))}
                  className="btn btn-ghost btn-sm btn-icon flex-shrink-0" title="Antwort entfernen" aria-label="weg"><Sym name="x" className="h-3.5 w-3.5" /></button>
              )}
            </div>
            {kind === "poll" && <TrackPicker value={o.item} onPick={(item) => setAt(i, { item })} />}
          </li>
        ))}
      </ul>
        {options.length < 4 && (
          <button type="button" onClick={() => setOptions((list) => [...list, { label: "", item: null, correct: false }])}
            className="btn btn-ghost btn-sm mt-2"><Sym name="plus" className="h-3.5 w-3.5" />Antwort dazu</button>
        )}
      </div>
      <label className="flex cursor-pointer items-center gap-2 text-sm text-secondary">
        <input type="checkbox" className="h-4 w-4" checked={asTemplate} onChange={(e) => setAsTemplate(e.target.checked)} />
        Als Vorlage merken (läuft nicht selbst)
      </label>
      <button type="submit" disabled={busy} className="btn btn-primary w-full">Anlegen</button>
      {kind === "poll" && (
        <p className="field-hint">
          Hängt an einer Antwort ein Titel aus der Mediathek, könnt ihr den Gewinner hinterher mit einem Klick einreihen.
        </p>
      )}
    </form>
  );
}

export function MitmachenPage(): JSX.Element {
  const { user } = useAuth();
  const mayRun = user?.role === "moderator" || user?.role === "tenant_admin" || user?.role === "super_admin";

  const [channels, setChannels] = useState<Channel[]>([]);
  const [slug, setSlug] = useState<string>("");
  const [modules, setModules] = useState<Record<string, { enabled: boolean; settings: Record<string, unknown> }>>({});
  const [tab, setTab] = useState<"poll" | "quiz">("poll");
  const [polls, setPolls] = useState<Poll[]>([]);
  const [pollTemplates, setPollTemplates] = useState<Poll[]>([]);
  const [quizzes, setQuizzes] = useState<QuizQuestion[]>([]);
  const [quizTemplates, setQuizTemplates] = useState<QuizQuestion[]>([]);
  const [scores, setScores] = useState<QuizScore[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | null>(null);

  const pollOn = modules.poll?.enabled === true;
  const quizOn = modules.quiz?.enabled === true;
  const winnerToQueue = modules.poll?.settings.winner_to_queue !== false;

  useEffect(() => {
    api.listChannels().then((list) => {
      setChannels(list);
      setSlug((s) => s || list[0]?.slug || "");
    }).catch((err) => setError(errorText(err)));
    api.listModules().then((list) => {
      setModules(Object.fromEntries(list.map((m) => [m.id, { enabled: m.enabled, settings: m.settings }])));
      if (!list.find((m) => m.id === "poll")?.enabled) setTab("quiz");
    }).catch((err) => setError(errorText(err)));
  }, []);

  const load = useCallback(async (): Promise<void> => {
    if (!slug) return;
    try {
      if (pollOn) {
        const [list, templates] = await Promise.all([pollApi.list(slug), pollApi.templates(slug)]);
        setPolls(list);
        setPollTemplates(templates);
      }
      if (quizOn) {
        const [list, templates, board] = await Promise.all([quizApi.list(slug), quizApi.templates(slug), quizApi.scoreboard(slug)]);
        setQuizzes(list);
        setQuizTemplates(templates);
        setScores(board.scores);
      }
    } catch (err) {
      setError(errorText(err));
    }
  }, [slug, pollOn, quizOn]);

  // Solange etwas läuft, alle fünf Sekunden nachsehen – dann wächst der Zähler mit.
  const running = useMemo(
    () => polls.some((p) => p.status === "open") || quizzes.some((q) => q.status === "open" || q.status === "revealed"),
    [polls, quizzes]
  );
  useEffect(() => {
    void load();
    if (timer.current) window.clearInterval(timer.current);
    timer.current = window.setInterval(() => void load(), running ? 5000 : 30000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [load, running]);

  const act = async (what: () => Promise<unknown>, message: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await what();
      setNotice(message);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const createPoll = (question: string, options: Draft[], asTemplate: boolean): void => {
    void act(
      () => pollApi.create(slug, {
        question,
        options: options.map((o) => ({ label: o.label, library_item_id: o.item?.id ?? null })),
        is_template: asTemplate,
      }),
      asTemplate ? "Vorlage gespeichert." : "Frage angelegt. Jetzt könnt ihr sie starten."
    );
  };

  const createQuiz = (question: string, options: Draft[], asTemplate: boolean): void => {
    void act(
      () => quizApi.create(slug, {
        question,
        options: options.map((o) => ({ label: o.label, is_correct: o.correct })),
        is_template: asTemplate,
      }),
      asTemplate ? "Vorlage gespeichert." : "Frage angelegt. Jetzt könnt ihr sie starten."
    );
  };

  if (!pollOn && !quizOn) {
    return (
      <ModulAus title="Mitmachen" icon="poll">
        Weder „Live-Abstimmung“ noch „Mitraten“ ist eingeschaltet. Das geht unter Aussehen → Mitmach-Module.
      </ModulAus>
    );
  }

  const openPoll = polls.find((p) => p.status === "open");
  const openQuiz = quizzes.find((q) => q.status === "open" || q.status === "revealed");

  // Reiter nur für eingeschaltete Module.
  const reiter: Array<{ value: "poll" | "quiz"; label: string }> = [];
  if (pollOn) reiter.push({ value: "poll", label: "Abstimmung" });
  if (quizOn) reiter.push({ value: "quiz", label: "Mitraten" });

  const stimmen = (n: number): string => (n === 1 ? "1 Stimme" : `${n} Stimmen`);

  /** Knopfreihe „Frage daraus / löschen“ einer Vorlage. */
  const vorlagenZeile = (key: string, frage: string, zusatz: string | null, fromTemplate: () => Promise<unknown>, remove: () => Promise<unknown>): JSX.Element => (
    <li key={key} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3">
      <span className="min-w-0 flex-1 text-sm">
        <span className="block font-medium text-primary">{frage}</span>
        {zusatz && <span className="block truncate text-xs text-muted">{zusatz}</span>}
      </span>
      <span className="flex items-center gap-1">
        <button disabled={!mayRun || busy} onClick={() => void act(fromTemplate, "Frage aus der Vorlage angelegt.")}
          className="btn btn-secondary btn-sm">Frage daraus</button>
        <button disabled={!mayRun || busy} onClick={() => void act(remove, "Vorlage gelöscht.")}
          className="btn btn-ghost btn-sm btn-icon" title="Vorlage löschen" aria-label="löschen"><Sym name="trash" className="h-3.5 w-3.5" /></button>
      </span>
    </li>
  );

  /** Die große Karte für das, was gerade läuft. */
  const liveKarte = (titel: string, frage: string, zahl: string, inhalt: JSX.Element, knoepfe: JSX.Element): JSX.Element => (
    <section className="card relative overflow-hidden brand-glow" style={{ boxShadow: "var(--shadow-card), 0 0 0 1.5px rgb(var(--brand-primary-rgb) / 0.55)" }}>
      <div className="flex flex-wrap items-start justify-between gap-3 p-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.1em] text-brand-primary">
            <LiveDot />{titel}
          </div>
          <h2 className="mt-1.5 text-lg font-semibold leading-snug text-primary">{frage}</h2>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold tabular-nums leading-none text-primary">{zahl.split(" ")[0]}</div>
          <div className="mt-1 text-xs text-muted">{zahl.split(" ").slice(1).join(" ")}</div>
        </div>
      </div>
      <div className="space-y-3 px-5 pb-5">{inhalt}</div>
      <div className="flex flex-wrap gap-2 border-t border-default bg-card/60 px-5 py-3">{knoepfe}</div>
    </section>
  );

  const nichtsLaeuft = (
    <div className="flex items-center gap-3 rounded-xl border border-dashed border-default px-5 py-4 text-sm text-muted">
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-input"><Sym name="play" className="h-4 w-4" /></span>
      Gerade läuft nichts. Legt rechts eine Frage an und startet sie unten, wenn es in der Sendung so weit ist.
    </div>
  );

  return (
    <Page className="max-w-6xl">
      <PageHeader
        eyebrow="Hörer"
        title="Mitmachen"
        description={<>
          Fragen vorbereiten, in der Sendung starten und das Ergebnis mitlaufen sehen. Dasselbe geht im Studio im Fach „Mitmachen“.
          {!mayRun && " Starten und Beenden dürfen nur Moderation und Admins."}
        </>}
        actions={<KanalWahl channels={channels} slug={slug} onChange={setSlug} />}
      >
        {reiter.length > 1 && <Tabs value={tab} onChange={setTab} items={reiter} />}
      </PageHeader>

      <Meldungen error={error} notice={notice} onClear={() => setNotice(null)} />

      {tab === "poll" && pollOn && (
        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="min-w-0 space-y-5">
          {openPoll ? liveKarte(
            "Läuft gerade",
            openPoll.question,
            stimmen(openPoll.total),
            <>
              {openPoll.options.map((o) => {
                const max = Math.max(...openPoll.options.map((x) => x.votes));
                return <ErgebnisBalken key={o.id} label={o.label} share={o.share} value={`${o.share} % (${o.votes})`} vorn={o.votes > 0 && o.votes === max} />;
              })}
            </>,
            <button disabled={!mayRun || busy} onClick={() => void act(() => pollApi.close(openPoll.id), "Abstimmung beendet.")}
              className="btn btn-primary"><Sym name="check" />Beenden</button>
          ) : nichtsLaeuft}

          <Card title="Fragen und Ergebnisse" padded={false} actions={polls.length > 0 ? <Badge>{polls.length}</Badge> : undefined}>
            {polls.length === 0 && (
              <div className="p-5">
                <EmptyState icon={<Sym name="poll" className="h-5 w-5" />} title="Noch nichts da." description="Legt rechts eine Frage an." />
              </div>
            )}
            {polls.length > 0 && polls.every((p) => p.status === "open") && (
              <p className="px-5 py-4 text-sm text-muted">Die laufende Frage steht oben; weitere gibt es noch nicht.</p>
            )}
            <ul className="divide-y divide-default">
              {polls.filter((p) => p.status !== "open").map((p) => (
                <li key={p.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <span className="min-w-0 flex-1 font-medium text-primary">{p.question}</span>
                    {p.status === "draft"
                      ? <StandSchild stand="entwurf">vorbereitet</StandSchild>
                      : <span className="flex items-center gap-2"><StandSchild stand="beendet">beendet</StandSchild><span className="text-xs tabular-nums text-muted">{stimmen(p.total)}</span></span>}
                  </div>
                  <div className="mt-3 space-y-2">
                    {p.options.map((o) => (
                      <ErgebnisBalken key={o.id} label={o.label} share={o.share} leer={p.status === "draft"}
                        value={p.status === "draft" ? "–" : `${o.share} % (${o.votes})`} vorn={o.id === p.winner_option_id} />
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {p.status === "draft" && (
                      <button disabled={!mayRun || busy} onClick={() => void act(() => pollApi.open(p.id), "Abstimmung läuft.")}
                        className="btn btn-primary btn-sm"><Sym name="play" className="h-3 w-3" />Starten</button>
                    )}
                    {p.status === "closed" && winnerToQueue && p.winner_option_id && (
                      <button disabled={!mayRun || busy} onClick={() => void act(() => pollApi.winnerToQueue(p.id), "Gewinnertitel ist eingereiht.")}
                        className="btn btn-secondary btn-sm"><Sym name="music" className="h-3.5 w-3.5" />Gewinner in die Queue</button>
                    )}
                    <button disabled={!mayRun || busy} onClick={() => void act(() => pollApi.remove(p.id), "Gelöscht.")}
                      className="btn btn-ghost btn-sm ml-auto"><Sym name="trash" className="h-3.5 w-3.5" />löschen</button>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
          </div>

          <aside className="min-w-0 space-y-5 lg:sticky lg:top-4">
          <QuestionForm kind="poll" busy={busy || !mayRun} onCreate={createPoll} />

          {pollTemplates.length > 0 && (
            <Card title="Vorlagen" padded={false} actions={<Badge>{pollTemplates.length}</Badge>}>
              <ul className="divide-y divide-default">
                {pollTemplates.map((t) => vorlagenZeile(
                  t.id, t.question, t.options.map((o) => o.label).join(", "),
                  () => pollApi.create(slug, { question: t.question, options: t.options.map((o) => ({ label: o.label, library_item_id: o.library_item_id })) }),
                  () => pollApi.remove(t.id),
                ))}
              </ul>
            </Card>
          )}
          </aside>
        </div>
      )}

      {tab === "quiz" && quizOn && (
        <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="min-w-0 space-y-5">
          {openQuiz ? liveKarte(
            openQuiz.status === "revealed" ? "Aufgelöst" : "Läuft gerade",
            openQuiz.question,
            `${openQuiz.correct_count} haben richtig getippt`,
            <>
              {openQuiz.options.map((o) => (
                <ErgebnisBalken key={o.id} share={o.share} value={o.votes} vorn={o.is_correct}
                  label={<span className="inline-flex items-center gap-1.5">{o.label}{o.is_correct && <Sym name="check" className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />}</span>} />
              ))}
            </>,
            <>
              {openQuiz.status === "open" && (
                <button disabled={!mayRun || busy} onClick={() => void act(() => quizApi.reveal(openQuiz.id), "Aufgelöst.")}
                  className="btn btn-primary"><Sym name="sparkles" />Auflösen</button>
              )}
              <button disabled={!mayRun || busy} onClick={() => void act(() => quizApi.close(openQuiz.id), "Frage beendet.")}
                className="btn btn-secondary">Beenden</button>
            </>
          ) : nichtsLaeuft}

          <Card title="Fragen und Ergebnisse" padded={false} actions={quizzes.length > 0 ? <Badge>{quizzes.length}</Badge> : undefined}>
            {quizzes.length === 0 && (
              <div className="p-5">
                <EmptyState icon={<Sym name="help" className="h-5 w-5" />} title="Noch nichts da." description="Legt rechts eine Frage an." />
              </div>
            )}
            {quizzes.length > 0 && !quizzes.some((q) => q.status === "draft" || q.status === "closed") && (
              <p className="px-5 py-4 text-sm text-muted">Die laufende Frage steht oben; weitere gibt es noch nicht.</p>
            )}
            <ul className="divide-y divide-default">
              {quizzes.filter((q) => q.status === "draft" || q.status === "closed").map((q) => (
                <li key={q.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <span className="min-w-0 flex-1 font-medium text-primary">{q.question}</span>
                    {q.status === "draft"
                      ? <StandSchild stand="entwurf">vorbereitet</StandSchild>
                      : <span className="flex items-center gap-2"><StandSchild stand="beendet">beendet</StandSchild><span className="text-xs tabular-nums text-muted">{q.correct_count} von {q.total} richtig</span></span>}
                  </div>
                  <p className="mt-2 flex items-center gap-1.5 text-sm text-secondary">
                    <Sym name="check" className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                    Richtig: <span className="font-medium text-primary">{q.options.find((o) => o.is_correct)?.label ?? "–"}</span>
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {q.status === "draft" && (
                      <button disabled={!mayRun || busy} onClick={() => void act(() => quizApi.open(q.id), "Die Frage läuft.")}
                        className="btn btn-primary btn-sm"><Sym name="play" className="h-3 w-3" />Starten</button>
                    )}
                    <button disabled={!mayRun || busy} onClick={() => void act(() => quizApi.remove(q.id), "Gelöscht.")}
                      className="btn btn-ghost btn-sm ml-auto"><Sym name="trash" className="h-3.5 w-3.5" />löschen</button>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
          </div>

          <aside className="min-w-0 space-y-5 lg:sticky lg:top-4">
          <QuestionForm kind="quiz" busy={busy || !mayRun} onCreate={createQuiz} />

          {scores.length > 0 && (
            <Card title="Bestenliste dieser Runde" description="Nur Spitzname und Punkte – mehr wird von den Hörern nicht gespeichert." padded={false}>
              <ol className="divide-y divide-default">
                {scores.map((s, i) => (
                  <li key={s.nickname} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                    <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-xs font-semibold tabular-nums ${i < 3 ? "bg-brand-primary/15 text-brand-primary" : "bg-input text-muted"}`}>{i + 1}</span>
                    <span className="min-w-0 flex-1 truncate text-primary">{s.nickname}</span>
                    <span className="tabular-nums text-muted">{s.points === 1 ? "1 Punkt" : `${s.points} Punkte`}</span>
                  </li>
                ))}
              </ol>
            </Card>
          )}

          {quizTemplates.length > 0 && (
            <Card title="Vorlagen" padded={false} actions={<Badge>{quizTemplates.length}</Badge>}>
              <ul className="divide-y divide-default">
                {quizTemplates.map((t) => vorlagenZeile(
                  t.id, t.question, null,
                  () => quizApi.create(slug, { question: t.question, options: t.options.map((o) => ({ label: o.label, is_correct: o.is_correct })) }),
                  () => quizApi.remove(t.id),
                ))}
              </ul>
            </Card>
          )}
          </aside>
        </div>
      )}
    </Page>
  );
}
